import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, PlayCircle } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import { coursesApi } from "@/lib/api";
import { formatDuration } from "@/lib/format";

export default function CoursesPage() {
    const [courses, setCourses] = useState([]);
    const [filter, setFilter] = useState("All");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        coursesApi
            .list()
            .then(setCourses)
            .catch(() => setCourses([]))
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => {
        const set = new Set(courses.map((c) => c.category));
        return ["All", ...Array.from(set).sort()];
    }, [courses]);

    const filtered = useMemo(() => {
        if (filter === "All") return courses;
        return courses.filter((c) => c.category === filter);
    }, [courses, filter]);

    return (
        <div className="bg-brand-offwhite" data-testid="courses-page">
            <section className="border-b border-brand-line py-20 md:py-28">
                <div className="container-x">
                    <Reveal>
                        <SectionLabel index={0} label="Catalog" />
                        <h1 className="mt-6 font-display text-6xl leading-[1.05] md:text-8xl">
                            THE <span className="text-brand-gold">CATALOG.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-brand-steel">
                            Structured learning pathways for the UK construction workforce — safety, certifications and skills.
                        </p>
                    </Reveal>
                    <div className="mt-10 flex flex-wrap gap-3" data-testid="courses-filters">
                        {categories.map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                                    filter === f
                                        ? "border-brand-gold bg-brand-gold text-brand-charcoal"
                                        : "border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-offwhite"
                                }`}
                                data-testid={`courses-filter-${f.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="container-x">
                    {loading ? (
                        <p
                            className="font-mono-tag text-brand-steel"
                            data-testid="courses-loading"
                        >
                            LOADING COURSES…
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                            {filtered.map((c, i) => (
                                <Reveal key={c.id} delay={i * 0.05}>
                                    <Link
                                        to={`/portal/courses/${c.slug}`}
                                        className="group block border border-brand-line bg-white transition-colors hover:border-brand-charcoal"
                                        data-testid={`course-card-${c.slug}`}
                                    >
                                        <div className="aspect-[16/10] overflow-hidden">
                                            <img
                                                src={c.cover_image}
                                                alt={c.title}
                                                className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                                            />
                                        </div>
                                        <div className="border-t border-brand-line p-6">
                                            <div className="flex items-center gap-3">
                                                <span className="border border-brand-gold px-2 py-1 font-mono-tag text-brand-gold">
                                                    {c.category}
                                                </span>
                                                <span className="font-mono-tag text-brand-steel">
                                                    {c.level}
                                                </span>
                                            </div>
                                            <h3 className="mt-4 font-display text-3xl uppercase leading-tight">
                                                {c.title}
                                            </h3>
                                            <p className="mt-3 font-body text-sm leading-relaxed text-brand-steel">
                                                {c.short_description}
                                            </p>
                                            <div className="mt-6 flex items-center justify-between border-t border-brand-line pt-4">
                                                <div className="flex items-center gap-4 text-brand-steel">
                                                    <span className="flex items-center gap-2 font-mono-tag">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        {formatDuration(c.duration_minutes)}
                                                    </span>
                                                    <span className="flex items-center gap-2 font-mono-tag">
                                                        <PlayCircle className="h-3.5 w-3.5" />
                                                        {c.lesson_count} lessons
                                                    </span>
                                                </div>
                                                <ArrowUpRight className="h-4 w-4 text-brand-charcoal transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                            </div>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))}
                            {filtered.length === 0 && (
                                <p className="col-span-full font-mono-tag text-brand-steel">
                                    NO COURSES IN THIS CATEGORY YET.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
